import {
  getSafeVideoDuration,
  getVideoDurationOptions,
  getVideoRatioOptions,
  getVideoResolutionOptions,
} from "~/common/constants"
import { VIDEO_MODELS } from "~/common/video/videoModels"
import type { JSONContent } from "@tiptap/core"
import {
  buildEditorContent,
  getPromptDocumentError,
  getPromptNodeText,
} from "~/common/promptDocument"
import { formatPromptUnderline } from "~/common/promptUnderline"
import type { UploadedResource } from "~/common/media/uploadedResources"
import type {
  VideoTemplate,
  TemplateReference,
  TemplateReferenceImage,
  TemplateReferenceAudio,
  TemplateTextField,
} from "./types"
import { createVideoTemplate, isPositiveTemplateRank } from "./template"
import { TEMPLATE_BADGE_CATEGORIES } from "./index"

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const readString = (value: unknown, name: string, fallback = ""): string => {
  if (value === undefined || value === null) return fallback
  if (typeof value !== "string") throw new Error(`${name}必须是文本`)
  return value
}

const readArray = (value: unknown, name: string): unknown[] => {
  if (value === undefined || value === null) return []
  if (!Array.isArray(value)) throw new Error(`${name}必须是数组`)
  return value
}

const readReferences = (value: unknown, label: string): TemplateReference[] =>
  readArray(value, `参考${label}`).map((item) => {
    if (!isRecord(item)) throw new Error(`参考${label}格式无效`)
    const id = readString(item.id, `${label}标识`)
    if (!id) throw new Error(`参考${label}缺少稳定标识`)
    return {
      id,
      url: readString(item.url, `${label}链接`),
      name: readString(item.name, `${label}名称`),
    }
  })

export const normalizeTemplateTags = (tags: string[]): string[] => [
  ...new Set(
    tags.map((tag) => tag.replace(/^[#＃\s]+/, "").trim()).filter(Boolean),
  ),
]

const readTemplateTags = (value: unknown): string[] =>
  (typeof value === "string" ? [value] : readArray(value, "标签")).map(
    (item) => {
      if (typeof item !== "string") throw new Error("标签必须是文本")
      return item
    },
  )

export const normalizeTemplateCategories = (categories: string[]): string[] =>
  Array.from(
    new Set(categories.map((category) => category.trim()).filter(Boolean)),
  )

// 兼容旧模板的单分类和独立角标；新保存只写 categories。
export const readTemplateCategories = (
  value: API.VideoTemplate.Content,
): string[] => {
  const categories =
    value.categories === undefined || value.categories === null
      ? normalizeTemplateCategories([readString(value.category, "分类")])
      : normalizeTemplateCategories(
          readArray(value.categories, "分类").map((item) => {
            if (typeof item !== "string") throw new Error("分类必须是文本")
            return item
          }),
        )
  const badge = readString(value.badge, "角标")
  if (badge && badge !== "hot" && badge !== "new")
    throw new Error("模板角标无效")
  if (
    !badge ||
    categories.includes(TEMPLATE_BADGE_CATEGORIES.hot) ||
    categories.includes(TEMPLATE_BADGE_CATEGORIES.new)
  )
    return categories
  return [...categories, TEMPLATE_BADGE_CATEGORIES[badge]]
}

// API items are open JSON objects. Validate them before exposing editable values.
export const readVideoTemplate = (
  value: API.VideoTemplate.Content,
): VideoTemplate => {
  const defaults = createVideoTemplate()
  if (value.materials != null && value.referenceImages == null)
    throw new Error("该模板仍使用旧版分组素材，需要先转换为有序参考图配置")
  const strings = Object.fromEntries(
    (
      [
        "templateId",
        "title",
        "videoUrl",
        "videoCover",
        "downloadUrl",
        "sameStyleUnavailableReason",
        "prompt",
      ] as const
    ).map((name) => [name, readString(value[name], name)]),
  ) as Pick<
    VideoTemplate,
    | "templateId"
    | "title"
    | "videoUrl"
    | "videoCover"
    | "downloadUrl"
    | "sameStyleUnavailableReason"
    | "prompt"
  >
  if (!strings.templateId.trim()) throw new Error("模板缺少 templateId")
  const rank = value.rank === undefined ? defaults.rank : value.rank
  // Keep legacy nonpositive ranks readable so Staff can repair stored records.
  if (typeof rank !== "number" || !Number.isFinite(rank))
    throw new Error("模板 rank 必须是有限数字")
  const model = readString(value.model, "模型")
  if (model && !Object.hasOwn(VIDEO_MODELS, model))
    throw new Error(`不支持的模型：${model}`)
  const ratio = readString(value.ratio, "比例")
  const resolution = readString(value.resolution, "分辨率")
  // Membership checks retain literal unions at this boundary.
  const ratios = ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"] as const
  const resolutions = ["480p", "720p", "768p", "1080p", "2K"] as const
  const selectedRatio = ratio ? ratios.find((item) => item === ratio) : null
  const selectedResolution = resolution
    ? resolutions.find((item) => item === resolution)
    : null
  if (selectedRatio === undefined || selectedResolution === undefined)
    throw new Error("模板比例或分辨率无效")
  const duration =
    value.duration === undefined ? defaults.duration : value.duration
  if (
    typeof duration !== "number" ||
    !Number.isFinite(duration) ||
    duration <= 0
  )
    throw new Error("模板时长必须大于 0")
  const available = value.sameStyleAvailable ?? false
  if (typeof available !== "boolean") throw new Error("开放状态必须是布尔值")
  const fields = readArray(value.fields, "文本变量").map((item) => {
    if (!isRecord(item)) throw new Error("文本变量格式无效")
    return {
      // Preserve legacy keys so existing {{name}} references keep working.
      name: readString(item.name, "变量名"),
      initialValue: readString(item.initialValue, "变量初始内容"),
    }
  })
  const referenceImages = readReferences(value.referenceImages, "图片")
  const referenceAudios = readReferences(value.referenceAudios, "音频")
  const motion = value.motionReference
  if (motion != null && !isRecord(motion))
    throw new Error("动作参考视频格式无效")
  return {
    ...defaults,
    ...strings,
    rank,
    categories: readTemplateCategories(value),
    model: model ? (model as API.Task.CreateVideoChannel) : null,
    ratio: selectedRatio,
    resolution: selectedResolution,
    duration,
    sameStyleAvailable: available,
    keywords: normalizeTemplateTags([
      ...readTemplateTags(value.keywords),
      ...readTemplateTags(value.caseType),
    ]),
    fields,
    referenceImages,
    referenceAudios,
    motionReference: isRecord(motion)
      ? {
          url: readString(motion.url, "动作视频链接"),
          cover: readString(motion.cover, "动作视频封面"),
        }
      : null,
  }
}

export const getConfigResources = (
  config: VideoTemplate,
): UploadedResource[] => [
  ...(config.motionReference?.url
    ? [
        {
          id: "video:motion-reference",
          url: config.motionReference.url,
          name: "动作参考视频",
          type: "video",
          uploadedAt: 0,
        },
      ]
    : []),
  ...config.referenceImages.map((image) => ({
    ...image,
    id: `image:${image.id}`,
    type: "image",
    uploadedAt: 0,
  })),
  ...config.referenceAudios.map((audio) => ({
    ...audio,
    id: `audio:${audio.id}`,
    type: "audio",
    uploadedAt: 0,
  })),
]

// Staff 编辑用的文档：变量框内是初始内容（为空时显示变量名占位）
export const getConfigEditorDocument = (config: VideoTemplate) =>
  buildEditorContent({
    mentionableResources: getConfigResources(config),
    value: config.prompt,
    fields: config.fields.map((field) => ({
      name: field.name,
      label: field.name,
      value: field.initialValue,
      required: false,
      maxLength: 0,
    })),
  })

const isUnderlined = (node: JSONContent) =>
  Boolean(node.marks?.some((mark) => mark.type === "underline"))

/**
 * 编辑器文档 -> 存储格式：变量框写回 {{变量名}}，框内文字作为初始内容；
 * 引用写回 @图片N；下划线写回 <u>…</u>。变量列表只来自文档中的变量框，
 * 手动输入的 {{名称}} 保持为普通文字，保存时会提示未配置变量。
 */
export const updateConfigPromptDocument = (
  config: VideoTemplate,
  document: JSONContent,
): VideoTemplate => {
  let text = ""
  const flags: boolean[] = []
  const separators: number[] = []
  const fields: TemplateTextField[] = []
  const append = (value: string, underline: boolean) => {
    text += value
    for (let index = 0; index < value.length; index++) flags.push(underline)
  }
  const appendSeparator = () => {
    separators.push(text.length)
    append("\n", false)
  }
  ;(document.content || []).forEach((paragraph, index) => {
    if (index) appendSeparator()
    for (const node of paragraph.content || []) {
      const underline = isUnderlined(node)
      if (node.type === "text") append(node.text || "", underline)
      else if (node.type === "hardBreak") appendSeparator()
      else if (node.type === "resourceMention")
        append(`@${node.attrs?.label || ""}`, underline)
      else if (node.type === "promptField") {
        const name = String(node.attrs?.name || "")
        append(`{{${name}}}`, underline)
        if (name && !fields.some((field) => field.name === name))
          fields.push({ name, initialValue: getPromptNodeText(node) })
      }
    }
  })
  // 换行两侧都有下划线时换行也算在内，避免把一段下划线拆成多段
  for (const index of separators)
    flags[index] = Boolean(flags[index - 1] && flags[index + 1])
  return { ...config, prompt: formatPromptUnderline(text, flags), fields }
}

export const getConfigPromptDocument = (config: VideoTemplate) =>
  buildEditorContent({
    mentionableResources: getConfigResources(config),
    value: config.prompt,
    fields: config.fields.map((field) => ({
      ...field,
      label: field.name,
      value: field.initialValue || field.name,
      required: false,
      maxLength: Number.MAX_SAFE_INTEGER,
    })),
  })

export const isTemplateMediaUrl = (value: string): boolean => {
  try {
    return ["http:", "https:"].includes(new URL(value).protocol)
  } catch {
    return false
  }
}

// 按案例视频时长生成模板默认时长：取整秒，已选默认模型时对齐到模型支持的时长
export const getTemplateDurationFromVideo = (
  model: VideoTemplate["model"],
  videoDuration: number,
): number => {
  const seconds = Math.max(1, Math.round(videoDuration))
  return model ? getSafeVideoDuration(model, seconds) : seconds
}

export const getVideoTemplateError = (config: VideoTemplate): string => {
  if (!config.templateId.trim()) return "请填写模板ID"
  if (!config.title.trim()) return "请填写模板标题"
  if (!isPositiveTemplateRank(config.rank)) return "模板排序值必须大于 0"
  if (!Number.isFinite(config.duration) || config.duration <= 0)
    return "模板时长必须大于 0"
  const urls = [
    config.videoUrl,
    config.videoCover,
    config.downloadUrl,
    config.motionReference?.url,
    config.motionReference?.cover,
    ...config.referenceImages.map((image) => image.url),
    ...config.referenceAudios.map((audio) => audio.url),
  ]
  if (urls.some((url) => url && !isTemplateMediaUrl(url)))
    return "素材链接必须是完整的 HTTP 或 HTTPS 地址"
  if (config.motionReference && !config.motionReference.url)
    return "请配置动作参考视频，或关闭动作参考视频选项"
  if (config.referenceImages.some((image) => !image.id || !image.url))
    return "请补齐参考图片"
  if (
    new Set(config.referenceImages.map((image) => image.id)).size !==
    config.referenceImages.length
  )
    return "参考图片标识不能重复"
  if (
    new Set(config.referenceImages.map((image) => image.url)).size !==
    config.referenceImages.length
  )
    return "参考图片不能重复"
  if (config.referenceAudios.some((audio) => !audio.id || !audio.url))
    return "请补齐参考音频"
  if (
    new Set(config.referenceAudios.map((audio) => audio.id)).size !==
    config.referenceAudios.length
  )
    return "参考音频标识不能重复"
  if (
    new Set(config.referenceAudios.map((audio) => audio.url)).size !==
    config.referenceAudios.length
  )
    return "参考音频不能重复"
  if (
    config.fields.some(
      (field) =>
        !field.name.trim() ||
        field.name !== field.name.trim() ||
        /[{}\r\n]/.test(field.name),
    )
  )
    return "请填写变量名，不能包含花括号、换行或首尾空格"
  if (config.fields.some((field) => /^已删除(图片|音频)\d+$/.test(field.name)))
    return "变量名不能使用系统保留的已删除素材名称"
  if (
    new Set(config.fields.map((field) => field.name)).size !==
    config.fields.length
  )
    return "变量名不能重复"
  if (!config.sameStyleAvailable) return ""
  if (!config.videoUrl) return "开放生成前请配置案例视频"
  if (!config.prompt.trim()) return "开放生成前请填写提示词"
  if (!config.referenceImages.length) return "开放生成前请配置参考图片"
  if (
    config.model &&
    config.ratio &&
    !getVideoRatioOptions(config.model).includes(config.ratio)
  )
    return "默认模型不支持该比例"
  if (
    config.model &&
    config.resolution &&
    !getVideoResolutionOptions(config.model).includes(config.resolution)
  )
    return "默认模型不支持该分辨率"
  if (
    config.model &&
    !getVideoDurationOptions(config.model).includes(config.duration)
  )
    return "默认模型不支持该时长"
  if (
    config.model &&
    config.referenceImages.length > VIDEO_MODELS[config.model].maxImageCount
  )
    return "参考图片超过默认模型的数量上限"
  if (
    config.model &&
    config.referenceAudios.length > VIDEO_MODELS[config.model].maxAudioCount
  )
    return "参考音频超过默认模型的数量上限，请更换模型或删减音频"
  const error = getPromptDocumentError(getConfigPromptDocument(config))
  if (error) return error
  const unknown = [...config.prompt.matchAll(/{{([^{}]+)}}/g)].find(
    (match) => !config.fields.some((field) => field.name === match[1]),
  )
  return unknown ? `提示词中存在未配置变量：${unknown[1]}` : ""
}

export const updateConfigPrompt = (
  config: VideoTemplate,
  prompt: string,
): VideoTemplate => {
  const previous = new Map(config.fields.map((field) => [field.name, field]))
  const fields: TemplateTextField[] = []
  const names = new Set<string>()
  for (const [, name] of prompt.matchAll(/{{([^{}]+)}}/g)) {
    if (
      names.has(name) ||
      !name.trim() ||
      name !== name.trim() ||
      /[\r\n]/.test(name) ||
      /^已删除(图片|音频)\d+$/.test(name)
    )
      continue
    names.add(name)
    fields.push(previous.get(name) ?? { name, initialValue: "" })
  }
  return { ...config, prompt, fields }
}

// 与 updateConfigPrompt 的识别规则保持一致，不合法的名称不会生成变量
export const getPromptVariableNameError = (name: string): string => {
  if (!name.trim()) return "请输入变量名"
  if (name !== name.trim()) return "变量名首尾不能有空格"
  if (/[{}\r\n]/.test(name)) return "变量名不能包含花括号或换行"
  if (/^已删除(图片|音频)\d+$/.test(name)) return "该变量名为系统保留名称"
  return ""
}

const updateConfigReferences = (
  config: VideoTemplate,
  references: TemplateReference[],
  type: "image" | "audio",
): VideoTemplate => {
  const key = type === "image" ? "referenceImages" : "referenceAudios"
  const label = type === "image" ? "图片" : "音频"
  const remap = (value: string) =>
    value.replace(new RegExp(`@${label}(\\d+)`, "g"), (match, text: string) => {
      const previous = config[key][Number(text) - 1]
      if (!previous) return match
      const index = references.findIndex((item) => item.id === previous.id)
      return index < 0 ? `{{已删除${label}${text}}}` : `@${label}${index + 1}`
    })
  return {
    ...config,
    [key]: references,
    prompt: remap(config.prompt),
    fields: config.fields.map((field) => ({
      ...field,
      initialValue: remap(field.initialValue),
    })),
  }
}

export const updateConfigImages = (
  config: VideoTemplate,
  images: TemplateReferenceImage[],
): VideoTemplate => updateConfigReferences(config, images, "image")

export const updateConfigAudios = (
  config: VideoTemplate,
  audios: TemplateReferenceAudio[],
): VideoTemplate => updateConfigReferences(config, audios, "audio")

const OBSOLETE_CONFIG_KEYS = new Set([
  // 已由 categories 取代
  "category",
  "badge",
  "alias",
  "hideOptions",
  "templateId",
  "caseTitle",
  "caseType",
  "aspectRatio",
  "sameStyleTemplate",
  "payload",
  "rows",
  "promptTemplate",
  "materials",
  "materialSuggestion",
  "productTitle",
  "sellingPoints",
  "coreSellingPoints",
])

// Preserve backend metadata; explicitly send empty values/arrays to clear old settings.
export const buildVideoTemplateContent = (
  config: VideoTemplate,
  original: API.VideoTemplate.Content = {},
): API.VideoTemplate.Content => {
  if (!isPositiveTemplateRank(config.rank))
    throw new Error("模板排序值必须大于 0")
  return {
    ...Object.fromEntries(
      Object.entries(original).filter(
        ([key]) => !OBSOLETE_CONFIG_KEYS.has(key),
      ),
    ),
    ...structuredClone(config),
    templateId: config.templateId.trim(),
    title: config.title.trim(),
    categories: normalizeTemplateCategories(config.categories),
    keywords: normalizeTemplateTags(config.keywords),
  }
}
